import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { CodeIcon } from "lucide-react";
import { Popper } from "@/components/atoms/Popper";
import { ToolCallCardCollapsibleJson } from "./ToolCallCardJson";
import { Icon } from "@/components/atoms/Icon";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";

// ── Show Raw JSON toggle button (header popover) ─────────────────────────────

export function ToolCallCardRawToggle({
  toolInput,
  toolOutput,
  toolError,
  isLoading = false,
}: {
  toolInput?: Record<string, unknown> | null;
  toolOutput?: Record<string, unknown> | null;
  toolError?: string | null;
  /** The call is still running: held in place, disabled, until it is done. */
  isLoading?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);

  if (isLoading) {
    return (
      <Button variant="ghost" size="icon-xs" disabled aria-label="Raw output">
        <Icon ReactIcon={CodeIcon} />
      </Button>
    );
  }

  // Check if there's any content to show
  const hasInput = toolInput && Object.keys(toolInput).length > 0;
  const hasOutput = toolOutput && Object.keys(toolOutput).length > 0;
  const hasError = !!toolError;
  const hasContent = hasInput || hasOutput || hasError;

  if (!hasContent) return null;

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Popper
        placement="bottom-end"
        isClickable={true}
        isEscapeable={true}
        isOpenState={[isOpen, setIsOpen]}
        floatingStyle={{
          minWidth: "300px",
          maxWidth: "400px",
          maxHeight: "400px",
          overflow: "auto",
          overscrollBehavior: "contain",
          padding: "8px",
        }}
        anchor={
          <Button variant="ghost" size="icon-xs">
            <Icon ReactIcon={CodeIcon} />
          </Button>
        }
      >
        <div className="flex flex-col gap-1">
          {hasInput && (
            <ToolCallCardCollapsibleJson
              label="Input"
              data={toolInput}
              color="var(--muted-foreground)"
            />
          )}
          {hasOutput && (
            <ToolCallCardCollapsibleJson
              label="Output"
              data={toolOutput}
              color="var(--foreground)"
            />
          )}
          {hasError && (
            <ToolCallCardCollapsibleJson
              label="Error"
              data={toolError}
              color="var(--destructive)"
            />
          )}
        </div>
      </Popper>
    </div>
  );
}

// ── Show Raw JSON toggle section (inline expandable) ─────────────────────────

export function ToolCallCardRawSection({
  toolInput,
  toolOutput,
  toolError,
}: {
  toolInput?: Record<string, unknown> | null;
  toolOutput?: Record<string, unknown> | null;
  toolError?: string | null;
}) {
  const [showRaw, setShowRaw] = useState(false);

  const hasContent =
    (toolInput && Object.keys(toolInput).length > 0) || toolOutput || toolError;
  if (!hasContent) return null;

  return (
    <div>
      <div
        onClick={() => setShowRaw(!showRaw)}
        className="inline-flex items-center gap-1 cursor-pointer text-muted-foreground/80 text-[11px] hover:text-muted-foreground"
      >
        <CodeIcon size="1em" style={{ fontSize: "12px" }} />
        <Typography level="body-xs" className="text-[10px]">
          {showRaw ? "Hide Raw" : "Show Raw"}
        </Typography>
      </div>

      <AnimatePresence>
        {showRaw && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ overflow: "hidden" }}
          >
            <div className="flex flex-col gap-1 mt-1">
              {toolInput && Object.keys(toolInput).length > 0 && (
                <ToolCallCardCollapsibleJson
                  label="Input"
                  data={toolInput}
                  color="var(--muted-foreground)"
                />
              )}
              {toolOutput && (
                <ToolCallCardCollapsibleJson
                  label="Output"
                  data={toolOutput}
                  color="var(--foreground)"
                />
              )}
              {toolError && (
                <ToolCallCardCollapsibleJson
                  label="Error"
                  data={toolError}
                  color="var(--destructive)"
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
